import React from "react";
import { Link } from "react-router-dom";
import RichText, { plainToHtml } from "./RichText";
import { fileUrl } from "../lib/api";
import { Check } from "lucide-react";

// Convert YouTube/Vimeo URL to embed URL
export function toEmbedUrl(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

const resolveImg = (data, urlKey = "image_url", pathKey = "image_path") =>
  data?.[pathKey] ? fileUrl(data[pathKey]) : (data?.[urlKey] || "");

const ContainerWrap = ({ children, alt = false, className = "" }) => (
  <section className={`${alt ? "bg-[#FAF3F4]" : "bg-white"} ${className}`}>
    <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-10 sm:py-14">{children}</div>
  </section>
);

function HeroBlock({ data }) {
  const bg = resolveImg(data);
  return (
    <section className="relative overflow-hidden bg-[#3a1116]" data-testid="hero-block">
      <div className="absolute inset-0 image-grain">
        {bg && <img src={bg} alt="" loading="eager" fetchpriority="high" className="w-full h-full object-cover opacity-70" />}
        <div className="hero-overlay" />
      </div>
      <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-32 sm:py-40 lg:py-52 text-white fade-in">
        {data.eyebrow && (
          <p className="label-eyebrow !text-[#F5C9CF] mb-6" data-testid="hero-eyebrow">{data.eyebrow}</p>
        )}
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl mb-8 !text-white" data-testid="hero-title">
          {data.title}
        </h1>
        {data.subtitle && (
          <p className="text-lg sm:text-xl text-white/85 max-w-2xl font-light leading-relaxed mb-10" data-testid="hero-subtitle">
            {data.subtitle}
          </p>
        )}
        {data.cta_text && (
          <Link to={data.cta_link || "/contact"} className="btn-inverted-burgundy" data-testid="hero-cta">
            {data.cta_text}
            <span aria-hidden>→</span>
          </Link>
        )}
      </div>
    </section>
  );
}

function HeadingBlock({ data }) {
  const level = data.level || "h2";
  const cls = level === "h1"
    ? "font-serif-display text-4xl sm:text-5xl lg:text-6xl mb-6 text-[#1a1a1a]"
    : level === "h2"
    ? "font-serif-display text-3xl sm:text-4xl lg:text-5xl mb-5 text-[#1a1a1a]"
    : "font-serif-display text-2xl sm:text-3xl mb-4 text-[#1a1a1a]";
  return React.createElement(level, { className: cls, "data-testid": "heading-block" }, data.text);
}

function TextBlock({ data }) {
  const html = plainToHtml(data.content || "");
  return <RichText html={html} className="prose-body max-w-3xl" />;
}

function ImageBlock({ data }) {
  const src = data.path ? fileUrl(data.path) : data.url;
  if (!src) return null;
  return (
    <figure className="my-4" data-testid="image-block">
      <img src={src} alt={data.alt || ""} loading="lazy" className="w-full max-w-4xl rounded-sm" />
      {data.caption && <figcaption className="mt-3 text-sm text-neutral-500 italic">{data.caption}</figcaption>}
    </figure>
  );
}

function VideoBlock({ data }) {
  const embed = toEmbedUrl(data.embed_url || data.url);
  if (!embed) return null;
  return (
    <div className="my-4 max-w-4xl" data-testid="video-block">
      <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-sm bg-neutral-100">
        <iframe
          src={embed}
          title={data.title || "Embedded video"}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {data.caption && <p className="mt-3 text-sm text-neutral-500 italic">{data.caption}</p>}
    </div>
  );
}

function QuoteBlock({ data }) {
  return (
    <blockquote className="my-4 max-w-3xl mx-auto text-center" data-testid="quote-block">
      <span className="block font-serif-display text-7xl text-[#6B1F2A] leading-none mb-2 select-none">"</span>
      <p className="font-serif-display text-3xl sm:text-4xl text-[#1a1a1a] italic leading-snug">{data.text}</p>
      {data.author && <cite className="block mt-6 text-xs not-italic tracking-[0.22em] uppercase text-[#6B1F2A]">— {data.author}</cite>}
    </blockquote>
  );
}

function StatsBlock({ data }) {
  const items = data.items || [];
  if (!items.length) return null;
  return (
    <div className="bg-[#6B1F2A] text-white" data-testid="stats-block">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
        {items.map((it, i) => (
          <div key={i} className="border-l border-white/20 pl-5">
            <p className="font-serif-display text-4xl sm:text-5xl text-white mb-2">{it.value}</p>
            <p className="text-xs uppercase tracking-[0.18em] text-white/70">{it.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ServiceGridBlock({ data }) {
  const items = data.items || [];
  if (!items.length) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="service-grid-block">
      {items.map((it, i) => {
        const img = it.image_path ? fileUrl(it.image_path) : it.image_url;
        const Wrapper = it.link ? Link : "div";
        const wrapperProps = it.link ? { to: it.link } : {};
        return (
          <Wrapper key={i} {...wrapperProps} className="group block bg-white border border-neutral-200 hover:border-[#6B1F2A] transition-colors overflow-hidden">
            {img && (
              <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
                <img src={img} alt={it.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            )}
            <div className="p-6">
              <h3 className="font-serif-display text-2xl text-[#1a1a1a] group-hover:text-[#6B1F2A] transition-colors mb-2">{it.title}</h3>
              <p className="text-sm text-neutral-600 leading-relaxed mb-4">{it.excerpt}</p>
              {it.link && <span className="label-eyebrow text-[#6B1F2A] inline-flex items-center gap-2">Learn more <span aria-hidden>→</span></span>}
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}

function CalloutBlock({ data }) {
  const burgundy = (data.variant || "burgundy") === "burgundy";
  return (
    <div className={`${burgundy ? "bg-[#6B1F2A] text-white" : "bg-[#FAF3F4]"} p-10 sm:p-16 my-4`} data-testid="callout-block">
      <div className="max-w-3xl">
        {data.eyebrow && <p className={`label-eyebrow mb-4 ${burgundy ? "!text-[#F5C9CF]" : ""}`}>{data.eyebrow}</p>}
        {data.title && (
          <h3 className={`font-serif-display text-3xl sm:text-4xl mb-5 ${burgundy ? "!text-white" : "text-[#1a1a1a]"}`}>{data.title}</h3>
        )}
        {data.text && (
          <p className={`whitespace-pre-line leading-relaxed ${burgundy ? "text-white/85" : "text-neutral-700"}`}>{data.text}</p>
        )}
        {data.cta_text && (
          <div className="mt-8">
            <Link to={data.cta_link || "/contact"} className={burgundy ? "btn-inverted-burgundy" : "btn-primary-burgundy"}>
              {data.cta_text}
              <span aria-hidden>→</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function TwoColumnBlock({ data }) {
  const img = resolveImg(data);
  const reverse = (data.side || "right") === "left"; // image on left
  return (
    <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "lg:[direction:rtl]" : ""}`} data-testid="two-column-block">
      <div className="lg:[direction:ltr]">
        {data.eyebrow && <p className="label-eyebrow mb-4">{data.eyebrow}</p>}
        {data.title && <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1a1a1a] mb-5 leading-tight">{data.title}</h2>}
        {data.text && <p className="text-neutral-700 leading-relaxed mb-6 whitespace-pre-line">{data.text}</p>}
        {data.cta_text && (
          <Link to={data.cta_link || "/contact"} className="btn-outline-burgundy">{data.cta_text}<span aria-hidden>→</span></Link>
        )}
      </div>
      {img && (
        <div className="lg:[direction:ltr]">
          <img src={img} alt={data.title || ""} loading="lazy" className="w-full aspect-[4/3] object-cover" />
        </div>
      )}
    </div>
  );
}

function PricingBlock({ data }) {
  const industryLabel = data.industry_label || "Industry Average";
  const industryValue = data.industry_value || "$5,000 – $20,000";
  const industryCaption = data.industry_caption || "What most agencies charge";
  const ourLabel = data.our_label || "Spirit & Associates";
  const ourValue = data.our_value || "$2,500";
  const ourCaption = data.our_caption || "Complete professional service";
  const savings = data.savings || "Save up to $17,500";
  return (
    <div className="my-4" data-testid="pricing-block">
      <div className="bg-white border-2 border-[#6B1F2A] rounded-sm overflow-hidden shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-neutral-200">
          <div className="p-8 sm:p-10 text-center bg-neutral-50">
            <p className="label-eyebrow text-neutral-500 mb-3">{industryLabel}</p>
            <p className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-neutral-400 line-through decoration-[1.5px] leading-tight">{industryValue}</p>
            {industryCaption && <p className="text-xs text-neutral-500 mt-3 tracking-wider uppercase">{industryCaption}</p>}
          </div>
          <div className="p-8 sm:p-10 text-center">
            <p className="label-eyebrow !text-[#6B1F2A] mb-3">{ourLabel}</p>
            <p className="font-serif-display text-5xl sm:text-6xl md:text-7xl text-[#6B1F2A] font-medium leading-none">{ourValue}</p>
            {ourCaption && <p className="text-xs !text-[#6B1F2A] mt-3 tracking-wider uppercase">{ourCaption}</p>}
          </div>
        </div>
        {savings && (
          <div className="bg-[#6B1F2A] text-white text-center py-4 px-4">
            <span className="font-serif-display text-xl sm:text-2xl !text-white">{savings}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ChecklistBlock({ data }) {
  const items = data.items || [];
  if (!items.length) return null;
  return (
    <div className="bg-[#FAF3F4] p-8 sm:p-12 lg:p-14 my-4" data-testid="checklist-block">
      {data.eyebrow && <p className="label-eyebrow mb-4">{data.eyebrow}</p>}
      {data.title && <h3 className="font-serif-display text-2xl sm:text-3xl text-[#1a1a1a] mb-8 leading-tight">{data.title}</h3>}
      <ul className="space-y-4 max-w-2xl">
        {items.map((it, i) => {
          const text = typeof it === "string" ? it : (it.text || "");
          return (
            <li key={i} className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#6B1F2A] text-white flex items-center justify-center mt-0.5">
                <Check size={16} strokeWidth={3} />
              </span>
              <span className="text-neutral-800 leading-relaxed pt-0.5">{text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Group consecutive non-full-bleed blocks together visually
const FULL_BLEED = new Set(["hero", "stats", "callout"]);

export default function BlockRenderer({ blocks = [] }) {
  if (!blocks?.length) return null;
  const rendered = [];
  let bucket = [];
  let altStripe = false;

  const flushBucket = () => {
    if (!bucket.length) return;
    const isAlt = altStripe;
    altStripe = !altStripe;
    rendered.push(
      <ContainerWrap key={`b-${rendered.length}`} alt={isAlt}>
        <div className="space-y-5">{bucket}</div>
      </ContainerWrap>
    );
    bucket = [];
  };

  blocks.forEach((block, idx) => {
    switch (block.type) {
      case "hero":
        flushBucket();
        rendered.push(<HeroBlock key={block.id || idx} data={block.data || {}} />);
        return;
      case "stats":
        flushBucket();
        rendered.push(<StatsBlock key={block.id || idx} data={block.data || {}} />);
        return;
      case "callout":
        flushBucket();
        rendered.push(<CalloutBlock key={block.id || idx} data={block.data || {}} />);
        return;
      case "heading":
        bucket.push(<HeadingBlock key={block.id || idx} data={block.data || {}} />); return;
      case "text":
        bucket.push(<TextBlock key={block.id || idx} data={block.data || {}} />); return;
      case "image":
        bucket.push(<ImageBlock key={block.id || idx} data={block.data || {}} />); return;
      case "video":
        bucket.push(<VideoBlock key={block.id || idx} data={block.data || {}} />); return;
      case "quote":
        bucket.push(<QuoteBlock key={block.id || idx} data={block.data || {}} />); return;
      case "service_grid":
        bucket.push(<ServiceGridBlock key={block.id || idx} data={block.data || {}} />); return;
      case "two_column":
        bucket.push(<TwoColumnBlock key={block.id || idx} data={block.data || {}} />); return;
      case "pricing":
        bucket.push(<PricingBlock key={block.id || idx} data={block.data || {}} />); return;
      case "checklist":
        bucket.push(<ChecklistBlock key={block.id || idx} data={block.data || {}} />); return;
      default: return;
    }
  });
  flushBucket();
  return <>{rendered}</>;
}
